import { filterLabel } from '../strings.locstring.json';
import loc from 'owa-localize';
import {
    AppState,
    Filter,
    isInSearchMode,
    isSearchQueryPersonaPill,
    getActiveLeftNavItemId,
    isInEVNavigationMode,
    isInDeepLinkMode,
    getActiveLeftNavItemSparseFolder,
} from '../../appstate';
import {
    createSelector,
    favoritesLeftNavItemId,
    ContactFolder,
    isContactsOrContactListsLeftNavItem,
    deletedItemsLeftNavItemId,
} from '../../shared';
import { getPersonaListViewModel } from './personaListViewModelSelector';
import type { PersonaListViewModel } from '../models/PersonaListViewModel';
import { toFilterName } from '../utils/personaListFilterUtils';
import { toSortingName } from '../utils/personaListSortUtils';

//  Show groups from HubList option drop down outside Favorites and All
export const getShowGroupsFromOptionsDropDown = createSelector<
    AppState,
    string,
    boolean,
    string,
    ContactFolder,
    boolean
>(
    'getHideGroupsFromOptionsDropDown',
    [
        (appState: AppState) => appState.view.navigationState.clickedLeftNavItemId,
        isInSearchMode,
        (appState: AppState) => appState.data.searchState.currentFolderId,
        (appState: AppState) => appState.data.contactFoldersState.rootContactFolder,
    ],
    (clickedLeftNavItemId, inSearchMode, searchFolderId, rootFolder) => {
        return (
            (clickedLeftNavItemId && clickedLeftNavItemId === favoritesLeftNavItemId) ||
            (clickedLeftNavItemId && rootFolder && clickedLeftNavItemId === rootFolder.id) ||
            (inSearchMode && searchFolderId && searchFolderId === favoritesLeftNavItemId) ||
            (inSearchMode && searchFolderId && rootFolder && searchFolderId === rootFolder.id)
        );
    }
);

// Hide filter from HubList option drop down for "contacts", "contact lists", "deleted" menu items and
// "contacts" and "contact lists" search filters in search
export const getHideFilterFromOptionsDropDown = createSelector<
    AppState,
    string,
    boolean,
    string,
    boolean
>(
    'getHideFilterFromOptionsDropDown',
    [
        getActiveLeftNavItemId,
        isInSearchMode,
        (appState: AppState) => appState.data.searchState.currentFolderId,
    ],
    (activeLeftNavItemId, inSearchMode, searchFolderId) => {
        return (
            isContactsOrContactListsLeftNavItem(activeLeftNavItemId) ||
            (inSearchMode &&
                searchFolderId &&
                isContactsOrContactListsLeftNavItem(searchFolderId)) ||
            activeLeftNavItemId === deletedItemsLeftNavItemId
        );
    }
);

// Hide HubList drop down for Favorites, and ContactLists in regular, search mode, in DeepLink mode, and for sparse folders
export const getHideOptionsDropDown = createSelector<
    AppState,
    string,
    boolean,
    boolean,
    string,
    boolean,
    boolean,
    ContactFolder,
    boolean
>(
    'getHideOptionsDropDown',
    [
        getActiveLeftNavItemId,
        isInSearchMode,
        isSearchQueryPersonaPill,
        (appState: AppState) => appState.data.searchState.currentFolderId,
        isInEVNavigationMode,
        isInDeepLinkMode,
        getActiveLeftNavItemSparseFolder,
    ],
    (
        activeLeftNavItemId,
        inSearchMode,
        searchQueryIsPersonaPill,
        searchFolderId,
        isInEVNavigationMode,
        isInDeepLinkMode,
        activeLeftNavItemSparseFolder
    ) => {
        if (isInEVNavigationMode || isInDeepLinkMode || activeLeftNavItemSparseFolder) {
            return true;
        }

        return (
            (activeLeftNavItemId && activeLeftNavItemId === favoritesLeftNavItemId) ||
            (inSearchMode &&
                (searchQueryIsPersonaPill ||
                    (searchFolderId && searchFolderId === favoritesLeftNavItemId)))
        );
    }
);

export const getIsFilterSelected = createSelector<
    AppState,
    string,
    boolean,
    string,
    PersonaListViewModel,
    boolean
>(
    'getIsFilterSelected',
    [
        getActiveLeftNavItemId,
        isInSearchMode,
        (appState: AppState) => appState.data.searchState.currentFolderId,
        getPersonaListViewModel,
    ],
    (activeLeftNavItemId, inSearchMode, searchFolderId, personaViewState) => {
        return (
            personaViewState.filter === Filter.All ||
            isContactsOrContactListsLeftNavItem(activeLeftNavItemId) ||
            (inSearchMode && isContactsOrContactListsLeftNavItem(searchFolderId))
        );
    }
);

export const getOptionsText = createSelector<
    AppState,
    string,
    boolean,
    PersonaListViewModel,
    string
>(
    'getOptionsText',
    [getActiveLeftNavItemId, isInSearchMode, getPersonaListViewModel],
    (activeLeftNavItemId, inSearchMode, personaViewState) => {
        if (inSearchMode && personaViewState.filter === Filter.All) {
            return loc(filterLabel);
        }

        const showSortingByText =
            personaViewState.filter === Filter.All ||
            isContactsOrContactListsLeftNavItem(activeLeftNavItemId);

        return showSortingByText
            ? toSortingName(personaViewState.sorting)
            : toFilterName(personaViewState.filter);
    }
);
